import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { ArrowLeft, Check, ChevronsUpDown } from 'lucide-react';
import { useLayoutState, useUiStore } from '../../stores/ui-store';
import { Search } from '../search';
import { SignOutDialog } from '../sign-out-dialog';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from '../ui/sidebar';
import { NavGroup } from './nav-group';
import { NavUser } from './nav-user';
import type { Brand, NavGroup as NavGroupType, SidebarView, User, UserMenuGroup } from './types';

/**
 * Sidebar-ul aplicaţiei.
 *
 * Faţă de shadcn-admin, unde componenta îşi citeşte datele direct din `sidebar-data`, aici
 * totul vine prin props — ca în edinro. Motivul e că meniul se filtrează după permisiunile
 * operatorului: dacă datele ar fi importate static, filtrarea ar trebui să se întâmple
 * *în* componenta de prezentare, iar un test pe ea ar cere randare.
 */

export type AppSidebarLabels = {
  search: string;
  signOut: string;
  signOutDescription: string;
  signOutConfirm: string;
  back: string;
  openProfileMenu: string;
};

const DEFAULT_LABELS: AppSidebarLabels = {
  search: 'Caută',
  signOut: 'Delogare',
  signOutDescription:
    'Sigur vrei să te deloghezi? Va trebui să te autentifici din nou ca să intri în aplicație.',
  signOutConfirm: 'Delogare',
  back: 'Înapoi',
  openProfileMenu: 'Deschide meniul de profil pentru',
};

type AppSidebarProps = {
  brand: Brand;
  navGroups: NavGroupType[];
  /** edinro: vederi drill-down; cea al cărei `basePath` se potriveşte înlocuieşte navigaţia. */
  views?: SidebarView[];
  user: User;
  userMenuGroups?: UserMenuGroup[];
  /** edinro: secţiunile stau deschise, fără chevron. */
  alwaysOpenSections?: boolean;
  /** edinro: conţinut fixat în footer, deasupra meniului de utilizator. */
  footerSlot?: React.ReactNode;
  labels?: Partial<AppSidebarLabels>;
  onSignOut: () => void;
};

function matchesBasePath(pathname: string, basePath: string) {
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
}

/** Linie punctată, ca separatoarele din edinro. `Separator` din shadcn e plin. */
function DashedRule() {
  return <div className="mx-3 border-t border-dashed" />;
}

export function AppSidebar({
  brand,
  navGroups,
  views = [],
  user,
  userMenuGroups = [],
  alwaysOpenSections = false,
  footerSlot,
  labels: labelsProp,
  onSignOut,
}: AppSidebarProps) {
  const labels = { ...DEFAULT_LABELS, ...labelsProp };
  const { collapsible, variant } = useLayoutState();
  const [signOutOpen, setSignOutOpen] = useState(false);

  const pathname = useLocation({ select: (location) => location.pathname });
  const activeView = useMemo(
    () =>
      views
        .filter((view) => matchesBasePath(pathname, view.basePath))
        .sort((a, b) => b.basePath.length - a.basePath.length)[0] ?? null,
    [views, pathname],
  );

  // Ţinem minte ultima rută din afara oricărei vederi: linkul „înapoi" duce acolo, nu la o
  // rută fixă. Altfel, cine intră în setări dintr-o fişă de client ar ieşi pe dashboard.
  const lastRootPath = useUiStore((state) => state.lastRootPath);
  const setLastRootPath = useUiStore((state) => state.setLastRootPath);

  useEffect(() => {
    if (!activeView) {
      setLastRootPath(pathname);
    }
  }, [activeView, pathname, setLastRootPath]);

  const isViewPath = (path: string) => views.some((view) => matchesBasePath(path, view.basePath));
  const backPath = lastRootPath && !isViewPath(lastRootPath) ? lastRootPath : (brand.href ?? '/');

  return (
    <Sidebar collapsible={collapsible} variant={variant}>
      <SidebarHeader className="gap-0 p-0">
        <SidebarBrand brand={brand} />
        <DashedRule />
      </SidebarHeader>

      <SidebarContent>
        {activeView ? (
          <>
            <SidebarViewHeader view={activeView} backPath={backPath} backLabel={labels.back} />
            {activeView.navGroups.map((group) => (
              <NavGroup key={group.title} {...group} indented alwaysOpen={alwaysOpenSections} />
            ))}
          </>
        ) : (
          <>
            <div className="px-3 pt-3">
              <Search placeholder={labels.search} />
            </div>
            {navGroups.map((group) => (
              <NavGroup key={group.title} {...group} alwaysOpen={alwaysOpenSections} />
            ))}
          </>
        )}
      </SidebarContent>

      <SidebarFooter className="p-0">
        {footerSlot ? <div className="px-3 pt-2">{footerSlot}</div> : null}
        <DashedRule />
        <NavUser
          user={user}
          groups={userMenuGroups}
          signOutLabel={labels.signOut}
          openMenuLabel={labels.openProfileMenu}
          onRequestSignOut={() => setSignOutOpen(true)}
        />
      </SidebarFooter>

      <SidebarRail />

      <SignOutDialog
        open={signOutOpen}
        onOpenChange={setSignOutOpen}
        onConfirm={onSignOut}
        title={labels.signOut}
        desc={labels.signOutDescription}
        confirmText={labels.signOutConfirm}
      />
    </Sidebar>
  );
}

/**
 * Brandul: titlu, subtitlu, și — când aplicaţia dă `brand.menu` — comutatorul de context.
 *
 * **Delogarea nu intră aici**, deşi edinro o pune. Ea stă în meniul de utilizator din
 * footer, iar un al doilea drum către ea doar întreabă operatorul, de fiecare dată, pe
 * care dintre ele trebuie să apese. Meniul de aici comută *contextul de lucru*, ceea ce e
 * altceva: e informaţia care trebuie văzută pe fiecare ecran, deci stă în capul aplicaţiei.
 */
function SidebarBrand({ brand }: { brand: Brand }) {
  const fallback = brand.title.charAt(0).toUpperCase();
  const { isMobile } = useSidebar();

  const BrandIcon = brand.icon;

  const face = (
    <>
      {/*
        Pătratul e **în opoziţie cu tema**: `--sidebar-primary` e închis pe temă luminoasă
        şi deschis pe cea întunecată, iar `-foreground` e inversul lui. Aşa reperul rămâne
        la fel de vizibil în ambele, fără culori scrise de mână pentru fiecare.
      */}
      <Avatar className="size-8 rounded-lg">
        <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground rounded-lg text-xs font-medium">
          {BrandIcon ? <BrandIcon className="size-4" /> : fallback}
        </AvatarFallback>
      </Avatar>
      <div className="block overflow-hidden text-start">
        <span className="truncate text-sm font-medium">{brand.title}</span>
        {brand.subtitle ? (
          <span className="block truncate text-xs text-muted-foreground">{brand.subtitle}</span>
        ) : null}
      </div>
    </>
  );

  const faceClass =
    'grid w-full grid-cols-[32px_1fr_16px] items-center gap-x-3 rounded-md p-1 pe-2 outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring';

  if (!brand.menu) {
    return (
      <div className="w-full p-3">
        <Link to={(brand.href ?? '/') as never} className={faceClass}>
          {face}
        </Link>
      </div>
    );
  }

  const menu = brand.menu;

  return (
    <div className="w-full p-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={menu.label}
            className={`${faceClass} data-[state=open]:bg-sidebar-accent`}
          >
            {face}
            <ChevronsUpDown className="size-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
          side={isMobile ? 'bottom' : 'right'}
          align="start"
          sideOffset={4}
        >
          <DropdownMenuLabel className="text-muted-foreground text-xs">
            {menu.label}
          </DropdownMenuLabel>
          {menu.items.map((item) => (
            <DropdownMenuItem
              key={item.id}
              onSelect={() => {
                // Alegerea deja activă nu se retrimite: ar fi o scriere fără efect, plus un
                // rând de jurnal despre o schimbare care nu s-a întâmplat.
                if (!item.checked) {
                  menu.onSelect(item.id);
                }
              }}
              className="gap-2"
            >
              {item.icon ? <item.icon className="size-4 shrink-0" /> : null}
              <span className="truncate">{item.label}</span>
              {item.checked ? <Check className="ms-auto size-4" /> : null}
            </DropdownMenuItem>
          ))}
          {menu.footer ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={menu.footer.href as never} className="gap-2">
                  {menu.footer.icon ? <menu.footer.icon className="size-4 shrink-0" /> : null}
                  {menu.footer.label}
                </Link>
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** edinro: antetul unei vederi drill-down — „← titlul vederii". */
function SidebarViewHeader({
  view,
  backPath,
  backLabel,
}: {
  view: SidebarView;
  backPath: string;
  backLabel: string;
}) {
  return (
    <div className="pt-3" data-testid="sidebar-view-header">
      <div className="px-2 pb-3">
        <Link
          to={backPath as never}
          aria-label={`${backLabel}: ${view.title}`}
          className="flex items-center rounded-md bg-sidebar-accent/50 outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring"
        >
          <div className="flex items-center gap-x-2.5 px-2 py-1">
            <ArrowLeft className="size-4 text-muted-foreground" />
            <span className="text-sm font-medium">{view.title}</span>
          </div>
        </Link>
      </div>
      <DashedRule />
    </div>
  );
}
